import React from 'react';
import {
  ListGroup,
  Badge,
  Button,
  Spinner,
  Row,
  Col,
  Card,
} from 'react-bootstrap';
import {
  FaEnvelope,
  FaTrash,
  FaEye,
  FaCheckDouble,
  FaUser,
  FaClock,
} from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTheme } from '../../contexts/ThemeContext';


const DiscussionList = ({ discussions, loading, onSelect, onDelete, onMarkAsRead, selectedId }) => {
  const { isDark } = useTheme();

  if (loading) {
    return (
      <div className="discussion-loading-state">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Chargement des discussions...</p>
      </div>
    );
  }

  if (!discussions || discussions.length === 0) {
    return (
      <div className="discussion-empty-state">
        <FaEnvelope size={48} className="mb-3" />
        <p>Aucune discussion trouvée</p>
      </div>
    );
  }


  const getOtherParticipant = (discussion) => {
    // API returns participant1 and participant2 separately
    // Return the first participant for display (usually the one who is not the admin)
    return discussion.participant1 || null;
  };


  return (
    <div className="discussion-list-container">
      {discussions.map((discussion) => {
        const otherParticipant = getOtherParticipant(discussion);
        const isSelected = selectedId === discussion.id;
        const unreadCount = discussion.unread_count || 0;
        const totalMessages = discussion.total_messages || 0;

        return (
          <Card
            key={discussion.id}
            className={`discussion-list-item mb-2 ${isSelected ? 'active' : ''}`}
            onClick={() => onSelect(discussion)}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body>
              <Row className="align-items-center">
                <Col xs={12} md={8}>
                  <div className="d-flex align-items-center gap-3">
                    <div className="position-relative">
                      {otherParticipant?.avatar ? (
                        <img
                          src={otherParticipant.avatar}
                          alt={otherParticipant.username}
                          className="rounded-circle"
                          style={{ width: 50, height: 50, objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle bg-secondary d-flex align-items-center justify-content-center"
                          style={{ width: 50, height: 50 }}
                        >
                          <FaUser className="text-white" />
                        </div>
                      )}
                      {unreadCount > 0 && (
                        <Badge
                          bg="danger"
                          className="position-absolute top-0 end-0 translate-middle"
                          pill
                        >
                          {unreadCount}
                        </Badge>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1">
                        {otherParticipant?.first_name && otherParticipant?.last_name 
                          ? `${otherParticipant.first_name} ${otherParticipant.last_name}`
                          : otherParticipant?.username || 'Utilisateur inconnu'}
                        {otherParticipant?.is_certified && (
                          <Badge bg="success" className="ms-2">✓</Badge>
                        )}
                      </h6>
                      <p className="mb-1 text-muted small text-truncate" style={{ maxWidth: 300 }}>
                        {discussion.last_message?.content || 'Aucun message'}
                      </p>
                      <small className="text-muted">
                        <FaClock className="me-1" />
                        {discussion.last_message_at
                          ? formatDistanceToNow(new Date(discussion.last_message_at), {
                              addSuffix: true,
                              locale: fr,
                            })
                          : 'Jamais'}
                      </small>
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={4} className="mt-3 mt-md-0">
                  <div className="d-flex justify-content-md-end gap-2 flex-wrap">
                    <Badge bg="info" className="d-flex align-items-center gap-1">
                      <FaEnvelope />
                      {totalMessages}
                    </Badge>
                    {unreadCount > 0 && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsRead(discussion.id, discussion.participant2?.id);
                        }}
                      >
                        <FaCheckDouble />
                      </Button>
                    )}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(discussion);
                      }}
                    >
                      <FaEye />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(discussion);
                      }}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );

};

export default DiscussionList;
